import React, { useState } from "react";
import { api, formatApiError, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { DownloadSimple, FilePdf, MicrosoftExcelLogo, CaretDown } from "@phosphor-icons/react";
import { toast } from "sonner";

/**
 * Branded export dropdown. Drop into any module page header:
 *   <ExportMenu module="hr" filters={{ status, department }} />
 *
 * Props:
 *   module   – one of: hr | compliance | projects | finance | tasks |
 *              assets | appointments | reports | approvals
 *   filters  – plain object of current filters; forwarded as query params
 *   label    – button text (default "Export")
 *   size     – passed to Button (default "sm")
 */
export default function ExportMenu({
  module,
  filters = {},
  label = "Export",
  size = "sm",
}) {
  const [busy, setBusy] = useState(null); // "pdf" | "xlsx" | null

  const download = async (fmt) => {
    setBusy(fmt);
    try {
      const params = {};
      Object.entries(filters || {}).forEach(([k, v]) => {
        if (v === undefined || v === null) return;
        if (v === "" || v === "all" || v === "any") return;
        params[k] = v;
      });
      const res = await api.get(`/exports/${module}.${fmt}`, {
        params,
        responseType: "blob",
      });
      const blob = new Blob([res.data], {
        type: fmt === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      // Try to read filename from Content-Disposition
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const filename = m ? m[1] : `descon_${module}.${fmt}`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported · ${filename}`);
    } catch (err) {
      // Blob error responses need special parsing
      if (err?.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const j = JSON.parse(text);
          toast.error(j.detail || "Export failed");
        } catch {
          toast.error("Export failed");
        }
      } else {
        toast.error(formatApiError(err));
      }
    } finally {
      setBusy(null);
    }
  };

  // Guard: don't render if user has no token (shouldn't happen inside the app)
  if (typeof window !== "undefined" && !getToken()) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size={size}
          className="rounded-sm gap-1.5"
          disabled={!!busy}
          data-testid={`export-menu-${module}`}
        >
          <DownloadSimple size={14} />
          {busy ? `Generating ${busy.toUpperCase()}…` : label}
          {!busy && <CaretDown size={12} />}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel className="font-mono-alt text-[10px] uppercase tracking-widest text-slate-500">
          Descon-branded export
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => download("pdf")}
          disabled={!!busy}
          data-testid={`export-${module}-pdf`}
        >
          <FilePdf size={16} className="mr-2 text-red-600" />
          <div className="flex flex-col">
            <span className="text-sm font-medium">PDF · letterhead</span>
            <span className="text-[10px] text-slate-500">A4 landscape, signed</span>
          </div>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => download("xlsx")}
          disabled={!!busy}
          data-testid={`export-${module}-xlsx`}
        >
          <MicrosoftExcelLogo size={16} className="mr-2 text-emerald-700" />
          <div className="flex flex-col">
            <span className="text-sm font-medium">Excel · branded</span>
            <span className="text-[10px] text-slate-500">Cover + data sheet</span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
